/**
 * JELinesTable — Phase 6a.16 extraction from Phase 6a.15a JECard.
 *
 * The Dr/Cr table primitive shared by the engagement-level Yevmiye tab
 * (each JECard renders its lines) AND the new PAJE tab (each PajeCard
 * renders its `suggestedJE` lines). The shape is identical
 * (`AggregatedJELine[]`) so the rendering is too.
 *
 * Layout:
 *   • TableHeader: Hesap | Borç | Alacak (i18n keys reused from `je.*`).
 *   • TableBody rows: account label (TR-mapped) + signed amounts, with
 *     `—` (em-dash) for the zero side.
 *   • TableFooter: TOPLAM row with summed debits + credits + ✓/⚠
 *     balanced indicator. Balance tolerance is 0.01 (covers the
 *     cumulative drift of summing currency strings — backend emits at
 *     most 2 decimals on Dr/Cr lines).
 *
 * The component is presentation-only: no state, no callbacks. `testIdPrefix`
 * lets the caller scope its `data-testid` attributes to its surface
 * (e.g. `je-card-001` or `paje-card-001`).
 */

import { useTranslation } from "react-i18next";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  mapAccountToTr,
  type TFn,
} from "@/pages/engagement/tabs/journalEntryComponents";
import type { AggregatedJELine } from "@/types";

export interface JELinesTableProps {
  lines: AggregatedJELine[];
  currency: string;
  /**
   * Prefix for `data-testid` attributes (e.g. `je-card-001` or
   * `paje-card-002`). The component appends `-line-<i>`, `-total`, and
   * `-balance` so tests can target rows uniquely across multiple
   * surfaces on the same page.
   */
  testIdPrefix: string;
}

export function JELinesTable({
  lines,
  currency,
  testIdPrefix,
}: JELinesTableProps) {
  const { t } = useTranslation();
  let debitSum = 0;
  let creditSum = 0;
  for (const line of lines) {
    debitSum += Number(line.debit) || 0;
    creditSum += Number(line.credit) || 0;
  }
  // 0.01 tolerance — currency amounts arrive as 2-decimal strings, so
  // cumulative drift across summing dozens of lines stays comfortably
  // below one cent.
  const balanced = Math.abs(debitSum - creditSum) < 0.01;
  return (
    <div className="overflow-x-auto w-full">
    <Table className="min-w-max">
      <TableHeader>
        <TableRow>
          <TableHead>{t("je.column.account")}</TableHead>
          <TableHead className="text-right">{t("je.column.debit")}</TableHead>
          <TableHead className="text-right">{t("je.column.credit")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {lines.map((line, i) => {
          const debit = Number(line.debit);
          const credit = Number(line.credit);
          return (
            <TableRow key={i} data-testid={`${testIdPrefix}-line-${i}`}>
              <TableCell>
                {mapAccountToTr(line.account, t as unknown as TFn)}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums">
                {debit > 0 ? formatCurrency(line.debit, currency) : "—"}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums">
                {credit > 0 ? formatCurrency(line.credit, currency) : "—"}
              </TableCell>
            </TableRow>
          );
        })}
        <TableRow
          className="border-t-2 font-bold"
          data-testid={`${testIdPrefix}-total`}
        >
          <TableCell className="font-bold border-t-2 border-border">
            {t("je.row.total")}
          </TableCell>
          <TableCell className="text-right font-mono tabular-nums font-bold border-t-2 border-border">
            {formatCurrency(debitSum.toFixed(2), currency)}
          </TableCell>
          <TableCell className="text-right font-mono tabular-nums font-bold border-t-2 border-border">
            {formatCurrency(creditSum.toFixed(2), currency)}{" "}
            <span
              className={cn(
                balanced
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-amber-600 dark:text-amber-400",
              )}
              data-testid={`${testIdPrefix}-balance`}
            >
              {balanced ? "✓" : "⚠"}
            </span>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
    </div>
  );
}
